import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const MBTIResults = ({ results, onRetakeTest }) => {
  const [showDetailedReport, setShowDetailedReport] = useState(false);

  if (!results || !results.score) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No results available</p>
      </div>
    );
  }

  const { score } = results;
  
  // Extract MBTI scores and type
  const mbtiScores = score.mbtiScores || score;
  const personalityType = score.personalityType || score.mbtiType || 'INTJ';
  
  const getTypeDescription = (type) => {
    const descriptions = {
      'INTJ': {
        name: 'The Architect',
        fullName: 'INTJ - The Architect',
        description: 'Imaginative and strategic thinkers with a plan for everything',
        characteristics: ['Strategic', 'Independent', 'Analytical', 'Determined', 'Curious', 'Visionary'],
        strengths: [
          'Strategic thinking and planning',
          'Independent and self-motivated',
          'Analytical problem solving',
          'Long-term vision and planning',
          'High standards and quality focus',
          'Continuous learning mindset'
        ],
        weaknesses: [
          'Can be overly critical',
          'May seem arrogant or dismissive',
          'Might struggle with emotions',
          'Can be perfectionist'
        ],
        workStyle: 'You excel at strategic planning and complex problem-solving. You prefer working independently on intellectually challenging projects that require deep analysis.',
        communicationStyle: 'Direct, logical, and precise. You prefer facts over emotions and appreciate efficiency in communication.',
        motivation: 'Intellectual challenges, strategic thinking, and achieving long-term goals motivate you most.',
        color: 'bg-purple-500',
        bgColor: 'bg-purple-50',
        textColor: 'text-purple-800',
        careerPaths: [
          'Strategic Planning',
          'Research & Development',
          'Systems Analysis',
          'Investment Banking',
          'Scientific Research',
          'Management Consulting'
        ]
      },
      'INTP': {
        name: 'The Logician',
        fullName: 'INTP - The Logician',
        description: 'Innovative inventors with an unquenchable thirst for knowledge',
        characteristics: ['Innovative', 'Analytical', 'Independent', 'Logical', 'Curious', 'Objective'],
        strengths: [
          'Creative problem solving',
          'Analytical thinking',
          'Independent research',
          'Logical reasoning',
          'Innovation and creativity',
          'Objective analysis'
        ],
        weaknesses: [
          'Can be overly theoretical',
          'May procrastinate on practical tasks',
          'Might seem detached',
          'Can be perfectionist'
        ],
        workStyle: 'You thrive in environments that allow creative problem-solving and independent research. You excel at developing new theories and innovative solutions.',
        communicationStyle: 'Precise, logical, and often theoretical. You enjoy deep intellectual discussions and exploring complex ideas.',
        motivation: 'Intellectual discovery, solving complex problems, and understanding how things work motivate you most.',
        color: 'bg-blue-500',
        bgColor: 'bg-blue-50',
        textColor: 'text-blue-800',
        careerPaths: [
          'Software Development',
          'Scientific Research',
          'Philosophy',
          'Engineering',
          'Data Analysis',
          'Academic Research'
        ]
      },
      'ENTJ': {
        name: 'The Commander',
        fullName: 'ENTJ - The Commander',
        description: 'Bold, imaginative and strong-willed leaders who always find a way',
        characteristics: ['Bold', 'Imaginative', 'Strong-willed', 'Strategic', 'Confident', 'Decisive'],
        strengths: [
          'Natural leadership abilities',
          'Strategic planning',
          'Quick decision making',
          'Confident communication',
          'Goal-oriented approach',
          'Efficient organization'
        ],
        weaknesses: [
          'Can be impatient',
          'May seem domineering',
          'Might overlook feelings',
          'Can be too direct'
        ],
        workStyle: 'You are a natural leader who excels at strategic planning and execution. You prefer taking charge and driving projects to completion.',
        communicationStyle: 'Direct, confident, and results-focused. You prefer clear, efficient communication that moves toward goals.',
        motivation: 'Achievement, leadership, strategic success, and making an impact motivate you most.',
        color: 'bg-red-500',
        bgColor: 'bg-red-50',
        textColor: 'text-red-800',
        careerPaths: [
          'Executive Leadership',
          'Management Consulting',
          'Entrepreneurship',
          'Project Management',
          'Strategic Planning',
          'Business Development'
        ]
      },
      'ENTP': {
        name: 'The Debater',
        fullName: 'ENTP - The Debater',
        description: 'Smart and curious thinkers who cannot resist an intellectual challenge',
        characteristics: ['Smart', 'Curious', 'Adaptable', 'Innovative', 'Energetic', 'Persuasive'],
        strengths: [
          'Quick thinking and adaptability',
          'Innovative problem solving',
          'Excellent communication',
          'Strategic thinking',
          'Enthusiasm and energy',
          'Creative solutions'
        ],
        weaknesses: [
          'Can be argumentative',
          'May lack follow-through',
          'Might seem scattered',
          'Can be too competitive'
        ],
        workStyle: 'You excel at brainstorming and finding innovative solutions. You prefer dynamic environments that allow creativity and intellectual challenge.',
        communicationStyle: 'Engaging, persuasive, and often challenging. You enjoy intellectual debates and exploring new ideas.',
        motivation: 'Intellectual challenge, innovation, variety, and making connections motivate you most.',
        color: 'bg-orange-500',
        bgColor: 'bg-orange-50',
        textColor: 'text-orange-800',
        careerPaths: [
          'Sales & Marketing',
          'Entrepreneurship',
          'Consulting',
          'Politics',
          'Media & Journalism',
          'Innovation Management'
        ]
      }
    };
    
    // Return a default description if the specific type isn't found
    return descriptions[type] || {
      name: 'Personality Type',
      fullName: `${type} - Personality Type`,
      description: 'Unique individual with distinct personality characteristics',
      characteristics: ['Individual', 'Unique', 'Complex', 'Dynamic'],
      strengths: ['Individual strengths and capabilities'],
      weaknesses: ['Areas for personal development'],
      workStyle: 'Your work style reflects your unique personality combination.',
      communicationStyle: 'Your communication style is shaped by your personality preferences.',
      motivation: 'Understanding your motivations can help guide your career and personal development.',
      color: 'bg-gray-500',
      bgColor: 'bg-gray-50',
      textColor: 'text-gray-800',
      careerPaths: ['Various career paths based on your interests and skills']
    };
  };

  const typeInfo = getTypeDescription(personalityType);

  // Calculate dimension scores
  const getDimensionScores = () => {
    const dimensions = [];
    
    if (mbtiScores.extraversion !== undefined && mbtiScores.introversion !== undefined) {
      dimensions.push({
        name: 'Extraversion vs Introversion',
        left: { label: 'Introversion', score: mbtiScores.introversion },
        right: { label: 'Extraversion', score: mbtiScores.extraversion },
        dominant: mbtiScores.extraversion > mbtiScores.introversion ? 'E' : 'I'
      });
    }
    
    if (mbtiScores.sensing !== undefined && mbtiScores.intuition !== undefined) {
      dimensions.push({
        name: 'Sensing vs Intuition',
        left: { label: 'Sensing', score: mbtiScores.sensing },
        right: { label: 'Intuition', score: mbtiScores.intuition },
        dominant: mbtiScores.intuition > mbtiScores.sensing ? 'N' : 'S'
      });
    }
    
    if (mbtiScores.thinking !== undefined && mbtiScores.feeling !== undefined) {
      dimensions.push({
        name: 'Thinking vs Feeling',
        left: { label: 'Thinking', score: mbtiScores.thinking },
        right: { label: 'Feeling', score: mbtiScores.feeling },
        dominant: mbtiScores.thinking > mbtiScores.feeling ? 'T' : 'F'
      });
    }
    
    if (mbtiScores.judging !== undefined && mbtiScores.perceiving !== undefined) {
      dimensions.push({
        name: 'Judging vs Perceiving',
        left: { label: 'Judging', score: mbtiScores.judging },
        right: { label: 'Perceiving', score: mbtiScores.perceiving },
        dominant: mbtiScores.judging > mbtiScores.perceiving ? 'J' : 'P'
      });
    }
    
    return dimensions;
  };

  const dimensionScores = getDimensionScores();

  if (showDetailedReport) {
    return (
      <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-lg p-6">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Detailed MBTI Assessment Report</h2>
          <p className="text-gray-600">Comprehensive personality profile analysis and career guidance</p>
        </div>

        {/* Executive Summary */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 mb-8">
          <div className="text-center">
            <div className={`inline-flex items-center justify-center w-24 h-24 ${typeInfo.color} text-white rounded-full text-3xl font-bold mb-4`}>
              {personalityType}
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">{typeInfo.fullName}</h3>
            <p className="text-gray-700 mb-4 text-lg">{typeInfo.description}</p>
            <div className="flex flex-wrap justify-center gap-2">
              {typeInfo.characteristics.map((char, index) => (
                <span key={index} className="bg-white px-4 py-2 rounded-full text-sm font-medium text-gray-700 shadow-sm">
                  {char}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* MBTI Dimensions */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Your MBTI Profile Dimensions</h3>
          <div className="space-y-6">
            {dimensionScores.map((dim, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6">
                <h4 className="font-semibold text-gray-800 mb-4 text-center">{dim.name}</h4>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-center flex-1">
                    <div className={`text-lg font-bold ${dim.dominant === dim.left.label.charAt(0) ? 'text-blue-600' : 'text-gray-500'}`}>
                      {dim.left.label}
                    </div>
                    <div className="text-sm text-gray-600">{dim.left.score}%</div>
                  </div>
                  <div className="text-center flex-1">
                    <div className={`text-lg font-bold ${dim.dominant === dim.right.label.charAt(0) ? 'text-blue-600' : 'text-gray-500'}`}>
                      {dim.right.label}
                    </div>
                    <div className="text-sm text-gray-600">{dim.right.score}%</div>
                  </div>
                </div>
                <div className="relative">
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-blue-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${Math.max(dim.left.score, dim.right.score)}%` }}
                    ></div>
                  </div>
                  <div className="absolute top-0 left-0 w-full h-3 flex justify-between items-center px-2">
                    <div className="w-2 h-2 bg-white rounded-full border-2 border-gray-300"></div>
                    <div className="w-2 h-2 bg-white rounded-full border-2 border-gray-300"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Detailed Analysis Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Strengths */}
          <div className="bg-green-50 rounded-lg p-6">
            <h4 className="font-semibold text-green-800 mb-4 text-lg">Key Strengths</h4>
            <ul className="space-y-3">
              {typeInfo.strengths.map((strength, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-green-500 mr-3 mt-1">✓</span>
                  <span className="text-gray-700">{strength}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Areas for Development */}
          <div className="bg-orange-50 rounded-lg p-6">
            <h4 className="font-semibold text-orange-800 mb-4 text-lg">Areas for Development</h4>
            <ul className="space-y-3">
              {typeInfo.weaknesses.map((weakness, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-orange-500 mr-3 mt-1">⚡</span>
                  <span className="text-gray-700">{weakness}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Work Style and Communication */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-blue-50 rounded-lg p-6">
            <h4 className="font-semibold text-blue-800 mb-4 text-lg">Work Style</h4>
            <p className="text-gray-700 leading-relaxed">{typeInfo.workStyle}</p>
          </div>

          <div className="bg-purple-50 rounded-lg p-6">
            <h4 className="font-semibold text-purple-800 mb-4 text-lg">Communication Style</h4>
            <p className="text-gray-700 leading-relaxed">{typeInfo.communicationStyle}</p>
          </div>
        </div>

        {/* Motivation */}
        <div className="bg-indigo-50 rounded-lg p-6 mb-8">
          <h4 className="font-semibold text-indigo-800 mb-4 text-lg">What Motivates You</h4>
          <p className="text-gray-700 leading-relaxed">{typeInfo.motivation}</p>
        </div>

        {/* Career Paths */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Recommended Career Paths</h3>
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {typeInfo.careerPaths.map((career, index) => (
                <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center gap-3">
                    <span className="text-blue-500 text-xl">💼</span>
                    <span className="font-medium text-gray-800">{career}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <button
            onClick={() => setShowDetailedReport(false)}
            className="flex-1 bg-gray-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
          >
            Show Summary View
          </button>
          <button
            onClick={onRetakeTest}
            className="flex-1 bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
          >
            Take Test Again
          </button>
          <Link
            to="/dashboard"
            className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-center"
          >
            Back to Dashboard
          </Link>
        </div>

        {/* Test Information */}
        <div className="pt-6 border-t border-gray-200">
          <div className="text-center text-sm text-gray-500">
            <p>Test completed on {new Date(results.takenAt).toLocaleDateString()}</p>
            <p>Your results are automatically saved and can be viewed anytime</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">MBTI Assessment Results</h2>
        <p className="text-gray-600">Your personality type analysis</p>
      </div>

      {/* Personality Type Display */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 mb-8 text-center">
        <div className={`inline-flex items-center justify-center w-20 h-20 ${typeInfo.color} text-white rounded-full text-2xl font-bold mb-4`}>
          {personalityType}
        </div>
        <h3 className="text-2xl font-bold text-gray-800 mb-2">{typeInfo.fullName}</h3>
        <p className="text-gray-700 mb-4">{typeInfo.description}</p>
        <div className="flex flex-wrap justify-center gap-2">
          {typeInfo.characteristics.slice(0, 4).map((char, index) => (
            <span key={index} className="bg-white px-3 py-1 rounded-full text-sm font-medium text-gray-700 shadow-sm">
              {char}
            </span>
          ))}
        </div>
      </div>

      {/* MBTI Dimensions */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Your MBTI Profile</h3>
        <div className="space-y-4">
          {dimensionScores.map((dim, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-3 text-center">{dim.name}</h4>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-600">{dim.left.label} ({dim.left.score}%)</span>
                <span className="text-sm font-medium text-gray-600">{dim.right.label} ({dim.right.score}%)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-blue-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${Math.max(dim.left.score, dim.right.score)}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Analysis */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Quick Analysis</h3>
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Strengths</h4>
              <ul className="text-gray-700 space-y-1">
                {typeInfo.strengths.slice(0, 3).map((strength, index) => (
                  <li key={index} className="flex items-center">
                    <span className="text-green-500 mr-2">✓</span>
                    {strength}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Work Style</h4>
              <p className="text-gray-700">
                {typeInfo.workStyle}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <button
          onClick={() => setShowDetailedReport(true)}
          className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors"
        >
          View Detailed Report
        </button>
        <button
          onClick={onRetakeTest}
          className="flex-1 bg-gray-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
        >
          Take Test Again
        </button>
        <Link
          to="/dashboard"
          className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-center"
        >
          Back to Dashboard
        </Link>
      </div>

      {/* Test Information */}
      <div className="pt-6 border-t border-gray-200">
        <div className="text-center text-sm text-gray-500">
          <p>Test completed on {new Date(results.takenAt).toLocaleDateString()}</p>
          <p>Your results are automatically saved and can be viewed anytime</p>
        </div>
      </div>
    </div>
  );
};

export default MBTIResults;
